import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@akuna/ui";
import { Button } from "@akuna/ui";
import { useTranslation } from "react-i18next";
import { useCreatePackageSubtype, type PackageSubtypeRow } from "@/hooks/usePackageSubtypes";

interface CreatePackageSubtypeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubtypeCreated?: (subtype: PackageSubtypeRow) => void;
}

export function CreatePackageSubtypeDialog({
  open,
  onOpenChange,
  onSubtypeCreated,
}: CreatePackageSubtypeDialogProps) {
  const { t } = useTranslation("erp");
  const createPackageSubtype = useCreatePackageSubtype();
  const schema = z.object({
    name: z.string().min(2, t("dialogs.createPackageSubtype.validationNameMin")),
  });

  type FormValues = z.infer<typeof schema>;
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: "" },
  });

  useEffect(() => {
    if (open) {
      form.reset({ name: "" });
    }
  }, [form, open]);

  const onSubmit = async (values: FormValues) => {
    try {
      const created = await createPackageSubtype.mutateAsync({ name: values.name });
      onSubtypeCreated?.(created);
      onOpenChange(false);
    } catch {
      // Mutation handles the error toast.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t("dialogs.createPackageSubtype.title")}</DialogTitle>
          <DialogDescription>{t("dialogs.createPackageSubtype.description")}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("dialogs.createPackageSubtype.name")}</FormLabel>
                  <FormControl>
                    <Input placeholder={t("dialogs.createPackageSubtype.placeholder")} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t("common:cancel")}
              </Button>
              <Button type="submit" disabled={createPackageSubtype.isPending}>
                {createPackageSubtype.isPending ? t("dialogs.createPackageSubtype.saving") : t("common:save")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
